import { GenexSISModel } from "@/models/sis/GenexSISModel";
import ClassGroup from "@/models/sis/ClassGroup";
import LessonPlan from "@/models/sis/LessonPlan";
import User from "@/models/sis/User";
import Subject from "@/models/sis/Subject";
import GenexHelperMixins from "@/mixins/GenexHelperMixins";
import {normalize} from "@/utils/data";

/**
 * A Lesson Plan
 * @property {number} id
 * @property {number} lesson_plan_id  - The id of the lesson plan associated with this lesson
 * @property {number} status_id  - The status of the lesson 1 planned 2 complete 3 unplanned
 * @property {number} course_scope_id - The id of the class associated with this lesson plan
 * @property {Object} courseScope - The courseScope this lesson plan belongs to
 * @property {number} campus_id - The id of the campus this lesson plan belongs to
 * @property {Object} campus - the campus this lesson plan belongs to
 * @property {string} starter_activity - The starter activity of the lesson
 * @property {string} engagement - The engagement of the lesson
 * @property {string} checkpoint - The checkpoint of the lesson
 * @property {string} resources - The resources string of the lesson
 * @property {Array.<number>} objective_ids - the ids of the objectives in this lesson plan
 * @property {Array.<Object>} objectives - The objectives in this lesson plan
 */
export default class SISLesson extends GenexSISModel {
  static entity = "SISLessons";

  static fields() {
    return {
      id: this.attr(null),
      lesson_plan_id: this.attr(null),
      class_group_id: this.attr(null),
      class_group: this.belongsTo(ClassGroup, "class_group_id"),
      lesson_plan: this.belongsTo(LessonPlan, "lesson_plan_id"),
      subject_id: this.attr(null),
      subject: this.belongsTo(Subject, "subject_id"),
      learner_ids: this.attr(() => []),
      learners: this.hasManyBy(User, "learner_ids"),
      status_id: this.attr(null),
      date: this.attr(null),
      note: this.attr(null),
      user_id: this.attr(null),
      user: this.belongsTo(User, "user_id"),
    };
  }

  /**
   * Returns all Lessons
   * @function
   * @param {Object} pagination
   * @param {number} pagination.page - Which page to retrieve
   * @param {number} pagination.limit - How many entities to retrieve
   * @param {Object} query
   * @param {Array.<string>} [relationships=[]] - Relationships to bring along
   * @returns {Promise<Response>} All the Lessons
   */

  static FetchAll({ page = 1, limit = 15 }, query = {}, relationships = []) {
    return this.api().get(`/lessons`, {
      ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
      params: {
        ...{
          page: page,
          limit: limit,
          with: relationships,
        },
        ...(query !== {} ? query : {}),
      },
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }

  /**
   * Returns a lesson
   * @function
   * @param {number} id - The id of the Lesson
   * @param {Array.<string>} [relationships=[]] - Relationships to bring along
   * @returns {Promise<Response>} the Lesson
   */

  static FetchById(id, relationships = []) {
    return this.api().get(`/lessons/${id}`, {
      ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
      params: {
        ...{
          with: relationships,
        },
      },
      dataTransformer: ({ data: { data } }) => {
        return normalize(data);
      },
    });
  }
}
